// Kořenová komponenta: React Router + QueryClientProvider
// Routes: / → LayoutMain, /select → StationSelectLayout, /station/:ghId → StationDetailLayout,
//         /station-charts → StationChartsLayout, /station-charts/:ghId → StationChartsDetailLayout
//         (zjednodušená mapa/detail pro variantu B, viz doc/plans/deploy-variant-b-station-charts.md),
//         /settings/password → SettingsPasswordLayout (změna vlastního hesla),
//         /reset-password → ResetPasswordLayout (nastavení hesla podle tokenu z e-mailu),
//         /admin/users → AdminUsersLayout (vyžaduje is_admin = 'Y'), /wip → NoDefinedLayout
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './api/queryClient'
import { LayoutMain } from './layouts/LayoutMain'
import { StationDetailLayout } from './layouts/StationDetailLayout'
import { StationSelectLayout } from './layouts/StationSelectLayout'
import { StationGroupLayout } from './layouts/StationGroupLayout'
import { MeteogramLayout } from './layouts/MeteogramLayout'
import { ForecastLayout } from './layouts/ForecastLayout'
import { StationChartsLayout } from './layouts/StationChartsLayout'
import { StationChartsDetailLayout } from './layouts/StationChartsDetailLayout'
import { SettingsPasswordLayout } from './layouts/SettingsPasswordLayout'
import { ResetPasswordLayout } from './layouts/ResetPasswordLayout'
import { RegisterLayout } from './layouts/RegisterLayout'
import { NoDefinedLayout } from './layouts/NoDefinedLayout'
import { AdminUsersLayout } from './layouts/AdminUsersLayout'
import { AdminStationGroupsLayout } from './layouts/AdminStationGroupsLayout'
import { AdminSchedulerLayout } from './layouts/AdminSchedulerLayout'
import { useAuthStore } from './store/useAuthStore'
import { useDeployConfigStore } from './store/useDeployConfigStore'
import { useVerifyToken } from './hooks/useVerifyToken'
import { useSessionWatch } from './hooks/useSessionWatch'
import { FillProgress } from './components/FillProgress'
import { FillPromptDialog } from './components/FillPromptDialog'
import { IncompleteDataBadge } from './components/IncompleteDataBadge'
import { LoginModal } from './components/auth/LoginModal'
import './utils/devCache' // registruje window.clear*Cache helpery

function AdminRoute({ element }) {
  const user = useAuthStore(s => s.user)
  const token = useAuthStore(s => s.token)
  // Pokud není token vůbec → přesměruj na hlavní stránku (tam je login)
  if (!token) return <Navigate to="/" replace />
  // Token existuje ale user ještě není načtený (ověření běží) → počkej
  if (!user) return null
  // Přihlášen, ale není admin → hlavní stránka
  if (user.is_admin !== 'Y') return <Navigate to="/" replace />
  return element
}

// Vyžaduje jen přihlášení (token) — pro /settings/password. Anonym nemá vlastní heslo.
// Nezávislé na variantě nasazení — nastavení hesla je vždy jen pro přihlášené.
function AuthRoute({ element }) {
  const token = useAuthStore(s => s.token)
  if (!token) return <Navigate to="/" replace />
  return element
}

// /meteogram — dostupnost řídí config nasazení (menuIcons pro aktuální roli), ne
// natvrdo token: varianta B nabízí meteogram i anonymovi (viz
// doc/plans/deploy-variants-a-b-c.md). Dokud config nedorazí (loaded=false), počkat —
// ne rovnou přesměrovat, jinak by anonym ve variantě B dostal false-negative redirect
// na fallbackových (A) hodnotách storu.
function MeteogramRoute({ element }) {
  const loaded = useDeployConfigStore(s => s.loaded)
  const menuIcons = useDeployConfigStore(s => s.menuIcons)
  if (!loaded) return null
  if (!menuIcons.includes('meteogram')) return <Navigate to="/" replace />
  return element
}

// /forecast — stejný vzor jako MeteogramRoute výše, dostupnost řízená menuIcons configu.
function ForecastRoute({ element }) {
  const loaded = useDeployConfigStore(s => s.loaded)
  const menuIcons = useDeployConfigStore(s => s.menuIcons)
  if (!loaded) return null
  if (!menuIcons.includes('forecast')) return <Navigate to="/" replace />
  return element
}

// Registrace dostupná jen když ji tahle instance nasazení povoluje (deploy config
// na serveru, viz doc/plans/deploy-variants-a-b-c.md D5) — jinak by šlo API zavolat
// přímo, ale server registrationEnabled:false stejně blokuje na /api/auth/register.
function RegisterRoute({ element }) {
  const registrationEnabled = useDeployConfigStore(s => s.registrationEnabled)
  if (!registrationEnabled) return <Navigate to="/" replace />
  return element
}

function App() {
  // Ověří uložený token při startu appky nezávisle na tom, jaká routa se otevře
  // (dřív to dělala jen UserIcon komponenta, která na admin stránkách není v DOM,
  // takže AdminRoute po refreshi zůstal navždy čekat na usera → prázdná stránka)
  useVerifyToken()
  // Hlídá vypršení přihlášení (login modal) a prodlužuje token při aktivitě.
  useSessionWatch()
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LayoutMain />} />
          <Route path="/select" element={<StationSelectLayout />} />
          <Route path="/station/:ghId" element={<StationDetailLayout />} />
          <Route path="/station/:ghId/:report" element={<StationDetailLayout />} />
          <Route path="/station-group" element={<StationGroupLayout />} />
          <Route path="/station-group/:product" element={<StationGroupLayout />} />
          <Route path="/meteogram" element={<MeteogramRoute element={<MeteogramLayout />} />} />
          <Route path="/forecast" element={<ForecastRoute element={<ForecastLayout />} />} />
          <Route path="/station-charts" element={<StationChartsLayout />} />
          <Route path="/station-charts/:ghId" element={<StationChartsDetailLayout />} />
          <Route path="/settings/password" element={<AuthRoute element={<SettingsPasswordLayout />} />} />
          <Route path="/reset-password" element={<ResetPasswordLayout />} />
          <Route path="/register" element={<RegisterRoute element={<RegisterLayout />} />} />
          <Route path="/admin/users" element={<AdminRoute element={<AdminUsersLayout />} />} />
          <Route path="/admin/station-groups" element={<AdminRoute element={<AdminStationGroupsLayout />} />} />
          <Route path="/admin/scheduler" element={<AdminRoute element={<AdminSchedulerLayout />} />} />
          <Route path="/wip" element={<NoDefinedLayout />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <FillProgress />
        <IncompleteDataBadge />
        <FillPromptDialog />
        <LoginModal />
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App